import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, MapPin, Instagram } from "lucide-react";

// ============================================
// LINK ORGANIZZATI PER CATEGORIA
// ============================================

const linkChiSiamo = [
  { label: "Associazione", href: "/associazione" },
  { label: "Tesseramento", href: "/tesseramento" },
  { label: "Amici", href: "/amici" },
];

const linkEsplora = [
  { label: "Attività", href: "/attivita" },
  { label: "Attestati", href: "/attestati" },
  { label: "Castelli", href: "/castelli" },
  { label: "L'Aquila e Cecilia", href: "/laquila-cecilia" },
];

const linkDocumenti = [
  { label: "Documenti CSI", href: "/documenti" },
  ,
  { label: "Trasparenza", href: "/trasparenza" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Termini e Condizioni", href: "/termini" },
];

// ============================================
// COMPONENTE FOOTER
// ============================================

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container-custom pt-16 pb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-8">
          {/* Column 1: Logo & Description (span 2 on lg) */}
          <div className="sm:col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/images/logo-footer.png"
                srcSet="/images/logo-footer.png 1x, /images/logo-footer@2x.png 2x, /images/logo-footer@3x.png 3x"
                alt="99 Castelli" 
                className="h-20 w-auto"
                width={259}
                height={80}
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="text-primary-foreground/80 font-body text-sm leading-relaxed mb-6">
              Associazione Sportiva Dilettantistica che promuove la scoperta dei castelli medievali abruzzesi attraverso
              escursioni, trekking ed eventi culturali.
            </p>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://facebook.com/99castelli"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center hover:bg-secondary/20 hover:text-secondary transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://instagram.com/99castelli"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center hover:bg-secondary/20 hover:text-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Column 2: Chi Siamo */}
          <div>
            <p className="text-lg font-heading font-semibold mb-4 text-secondary">Chi Siamo</p>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/"
                  className="text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                >
                  Home
                </Link>
              </li>
              {linkChiSiamo.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Esplora */}
          <div>
            <p className="text-lg font-heading font-semibold mb-4 text-secondary">Esplora</p>
            <ul className="space-y-3">
              {linkEsplora.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Documenti */}
          <div>
            <p className="text-lg font-heading font-semibold mb-4 text-secondary">Documenti</p>
            <ul className="space-y-3">
              {linkDocumenti.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Contatti */}
          <div>
            <p className="text-lg font-heading font-semibold mb-4 text-secondary">Contatti</p>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+393402894567"
                  className="flex items-center gap-3 text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                >
                  <Phone size={16} className="flex-shrink-0" />
                  340 2894567
                </a>
              </li>
              <li>
                <a
                  href="mailto:99castelli@gmail.com"
                  className="flex items-center gap-3 text-primary-foreground/80 hover:text-secondary transition-colors font-body text-sm"
                >
                  <Mail size={16} className="flex-shrink-0" />
                  <span className="break-all">99castelli@gmail.com</span>
                </a>
              </li>
              <li className="flex items-center gap-3 text-primary-foreground/80 font-body text-sm">
                <MapPin size={16} className="flex-shrink-0" />
                L'Aquila, Abruzzo
              </li>
            </ul>
          </div>
        </div>

        {/* Dati fiscali - sopra la linea di separazione */}
        <div className="flex justify-center mt-6 pb-0">
          <p className="text-sm text-primary-foreground/60 font-body text-center">
            C.F. 93116590667 • Cod. ASD: 06700181 • IBAN: IT74T0832740700000000008756
          </p>
        </div>
      </div>

      {/* Bottom Bar - solo copyright */}
      <div className="border-t border-primary-light">
        <div className="container-custom py-6">
          <div className="flex justify-center text-sm text-primary-foreground/60 font-body text-center">
            <p>© {currentYear} ASD 99Castelli.it | Tutti i diritti riservati.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
